import { webStorageUtil } from '@/state/webStorage.state.service';
import { getEnvVariable } from '@/lib/getEnvVariable';

import {
  ELocalStorageKeys,
  ELoginMethods,
  EPlatformType,
  EPublisherEvents,
  ESessionMetadataSource,
  ESessionStorageKeys,
  ESource
} from './enums';
import { EventSharedProps } from './types';
import { getUtmParams } from './utils';

/**
 * device-detector-js is 534 KB of user-agent regexes used only to stamp
 * brand/model/os fields onto analytics events. Loaded off the critical path
 * (on idle, right after this module is first evaluated) and parsed once;
 * an event fired before the parse lands simply omits the device fields, the
 * same fields it would omit for an unrecognised user agent.
 */
type DeviceInfo = {
  device?: { brand?: string; model?: string } | null;
  os?: { name?: string; version?: string } | null;
  client?: { name?: string; version?: string } | null;
};

let cachedDeviceInfo: DeviceInfo = {};

if (typeof window !== 'undefined') {
  const loadDeviceInfo = (): void => {
    void import('device-detector-js').then(
      ({ default: DeviceDetector }) => {
        cachedDeviceInfo = new DeviceDetector().parse(navigator.userAgent);
      },
      () => undefined // events keep flowing without device fields
    );
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(loadDeviceInfo);
  } else {
    setTimeout(loadDeviceInfo, 1);
  }
}

export const getSessionMetadataSource = (pathname?: string) => {
  const storedSource = webStorageUtil.get(ESessionStorageKeys.SOURCE);
  let sessionMetadataSource: string = ESessionMetadataSource.LOGIN_BUTTON_CLICKED;
  if (storedSource === ESource.STORE_TAB || pathname?.includes('/store')) {
    sessionMetadataSource = ESessionMetadataSource.STORE_TAB_CLICKED;
  }

  return sessionMetadataSource;
};

const resolveAnalyticsEnvironment = (value: string | undefined): string | undefined =>
  value && value !== 'ephemeral' ? value : undefined;

export const getAnalyticsEnvironment = (): string =>
  resolveAnalyticsEnvironment(getEnvVariable('NEXT_PUBLIC_ENVIRONMENT', process.env.NEXT_PUBLIC_ENVIRONMENT))
  ?? resolveAnalyticsEnvironment(getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV))
  ?? (getEnvVariable('NODE_ENV', process.env.NODE_ENV) === 'production' ? 'production' : 'local');

const getFieldName = (camelCaseName: string, isMixpanel: boolean): string => {
  if (!isMixpanel) {
    return camelCaseName;
  }

  return camelCaseName.replace(/([A-Z])/g, '_$1').toLowerCase();
};

export const getEventsSharedProps = (isMixpanel: boolean = false): EventSharedProps => {
  const deviceInfo = cachedDeviceInfo;
  const timestamp = new Date().getTime();
  const sessionId = webStorageUtil.get(ESessionStorageKeys.SESSION_DATA)?.sessionId;
  const utmParams = getUtmParams();
  const publisherId = webStorageUtil.get(ELocalStorageKeys.CURRENT_STORE_ID);
  const portalId = webStorageUtil.get(ELocalStorageKeys.PORTAL_ID);

  const baseProps: Record<string, any> = {
    [getFieldName('sessionId', isMixpanel)]: sessionId,
    timestamp,
    source: 'game-portal',
    [getFieldName('publisherId', isMixpanel)]: publisherId,
    [getFieldName('portalId', isMixpanel)]: portalId,
    [getFieldName('deviceManufacturer', isMixpanel)]: deviceInfo.device?.brand,
    [getFieldName('deviceModel', isMixpanel)]: deviceInfo.device?.model,
    os: deviceInfo.os?.name,
    [getFieldName('osVersion', isMixpanel)]: deviceInfo.os?.version,
    browser: deviceInfo.client?.name,
    [getFieldName('browserVersion', isMixpanel)]: deviceInfo.client?.version,
    [getFieldName('appVersion', isMixpanel)]: process.env.NEXT_PUBLIC_APP_VERSION
  };

  const utmProps: Record<string, string> = {};
  if (utmParams.utm_source) utmProps[getFieldName('utmSource', isMixpanel)] = utmParams.utm_source;
  if (utmParams.utm_medium) utmProps[getFieldName('utmMedium', isMixpanel)] = utmParams.utm_medium;
  if (utmParams.utm_campaign) utmProps[getFieldName('utmCampaign', isMixpanel)] = utmParams.utm_campaign;

  return {
    ...baseProps,
    ...utmProps
  };
};

export const getLoginEventsData = ({
  platform,
  method,
  options
}: {
  platform?: EPlatformType;
  method?: ELoginMethods;
  options?: Record<string, string>;
} = {}) => {
  const sharedProps = getEventsSharedProps();

  return {
    timestamp: sharedProps.timestamp,
    session_id: sharedProps.sessionId,
    device_platform: platform,
    method,
    sessionMetadata_source: getSessionMetadataSource(),
    utmSource: sharedProps.utmSource,
    utmMedium: sharedProps.utmMedium,
    utmCampaign: sharedProps.utmCampaign,
    ...options
  };
};

export const enrichOptions = (options: Record<string, any>, isMixpanel: boolean = false) => {
  const playerData = webStorageUtil.getAny(ELocalStorageKeys.PLAYER_DATA);
  const sessionData = webStorageUtil.getAny(ESessionStorageKeys.SESSION_DATA);
  const enrichedOptions: Record<string, any> = {
    ...options,
    ...getEventsSharedProps(isMixpanel)
  };

  if (playerData?.playerId && !enrichedOptions.playerId && !enrichedOptions.player_id) {
    enrichedOptions[getFieldName('playerId', isMixpanel)] = playerData.playerId;
  }
  if (playerData?.playerName && !enrichedOptions.playerName && !enrichedOptions.player_name) {
    enrichedOptions[getFieldName('playerName', isMixpanel)] = playerData.playerName;
  }
  if (playerData?.playerCountry && !enrichedOptions.playerCountry && !enrichedOptions.player_country) {
    enrichedOptions[getFieldName('playerCountry', isMixpanel)] = playerData.playerCountry;
  }

  if (sessionData && !enrichedOptions.sessionId && !enrichedOptions.session_id) {
    enrichedOptions[getFieldName('sessionId', isMixpanel)] = sessionData.id;
  }

  if (Object.values(EPublisherEvents).includes(options.eventName)) {
    enrichedOptions.source = 'browser app';
  }

  return enrichedOptions;
};
